import AddExtraIncomeModal from '../modals/AddExtraIncomeModal';
import MarkExtraIncomeAsPaidModal from '../modals/MarkExtraIncomeAsPaidModal';
import ExtraIncomeTable from '../components/ExtraIncomeTable';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ExtraIncome } from '../types';
import { fetchWithAuth } from '../util/fetchWithAuth';
import { formatCurrency } from '../util/formatCurrency';

const PAYMENT_STATUSES = ['all', 'unpaid', 'paid'] as const;
type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

const isPaymentStatus = (value: string | null): value is PaymentStatus =>
  PAYMENT_STATUSES.includes(value as PaymentStatus);

const SUMMARY_LABELS: Record<PaymentStatus, (count: number) => string> = {
  unpaid: count => `outstanding across ${count} unpaid`,
  paid: count => `collected across ${count} paid`,
  all: count => `across ${count} extra income entr${count === 1 ? 'y' : 'ies'}`
};

const ExtraIncomes: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [extraIncomes, setExtraIncomes] = useState<ExtraIncome[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openActionMenuId, setOpenActionMenuId] = useState<number | null>(null);
  const [selectedExtraIncome, setSelectedExtraIncome] = useState<ExtraIncome | null>(null);
  const [isMarkAsPaidModalOpen, setIsMarkAsPaidModalOpen] = useState(false);
  const [selectedExtraIncomeForPayment, setSelectedExtraIncomeForPayment] = useState<ExtraIncome | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [totalPages, setTotalPages] = useState(1);
  const [summaryTotal, setSummaryTotal] = useState('0');
  const [summaryCount, setSummaryCount] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);

  const statusParam = searchParams.get('payment_status');
  const paymentStatus: PaymentStatus = isPaymentStatus(statusParam) ? statusParam : 'all';

  const parsedPage = parseInt(searchParams.get('page') || '1', 10);
  const currentPage = isNaN(parsedPage) || parsedPage < 1 ? 1 : parsedPage;

  const refresh = () => setRefreshKey(key => key + 1);

  const setPage = (page: number) => {
    setSearchParams(prev => { prev.set('page', String(page)); return prev; });
  };

  const applySearch = (search: string) => {
    setAppliedSearch(search);
    setPage(1);
    // Search is an explicit action, so re-fetch even when the term is unchanged.
    refresh();
  };

  const applyPaymentStatus = (status: PaymentStatus) => {
    setSearchParams(prev => {
      prev.set('payment_status', status);
      prev.set('page', '1');
      return prev;
    });
  };

  useEffect(() => {
    const fetchExtraIncomes = async () => {
      setIsLoading(true);
      try {
        const query = new URLSearchParams();
        if (appliedSearch) query.append('search', appliedSearch);
        if (paymentStatus !== 'all') query.append('payment_status', paymentStatus);
        query.append('page', String(currentPage));

        const res = await fetchWithAuth(`/extra_incomes?${query.toString()}`);
        const data = await res.json();
        setExtraIncomes(data.extra_incomes ?? []);
        setTotalPages(data.total_pages);
        setSummaryTotal(data.summary?.total_amount ?? '0');
        setSummaryCount(data.summary?.count ?? 0);
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchExtraIncomes();
  }, [appliedSearch, paymentStatus, currentPage, refreshKey]);

  return (
    <div className="px-4 md:px-6 py-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
        <h1 className="text-2xl font-bold text-gray-800">Extra Income</h1>
        <button
          onClick={() => setIsModalOpen(true)}
          className="w-full sm:w-auto bg-black text-white px-4 py-2 rounded-md font-semibold hover:bg-gray-800"
        >
          Add Extra Income
        </button>
      </div>

      <div className="flex flex-col md:flex-row items-stretch md:items-center gap-2 mb-2">
        <input
          type="text"
          placeholder="Search by Reference or Customer"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              applySearch(searchTerm);
            }
          }}
          className="border px-4 py-2 rounded-md w-full md:w-80"
        />
        <select
          aria-label="Payment status"
          value={paymentStatus}
          onChange={(e) => applyPaymentStatus(e.target.value as PaymentStatus)}
          className="border px-4 py-2 rounded-md w-full md:w-40"
        >
          <option value="all">All</option>
          <option value="unpaid">Unpaid</option>
          <option value="paid">Paid</option>
        </select>
        <button
          onClick={() => applySearch(searchTerm)}
          className="w-full sm:w-auto bg-black text-white px-4 py-2 rounded-md font-semibold hover:bg-gray-800"
        >
          Search
        </button>
        {searchTerm && (
          <button
            onClick={() => {
              setSearchTerm('');
              applySearch('');
            }}
            className="w-full sm:w-auto bg-gray-300 text-gray-800 px-4 py-2 rounded-md font-semibold hover:bg-gray-400"
          >
            Clear
          </button>
        )}
      </div>

      <p className="text-sm text-gray-600 mb-4">
        <span className="font-semibold text-gray-900">{formatCurrency(parseFloat(summaryTotal))}</span>{' '}
        {SUMMARY_LABELS[paymentStatus](summaryCount)}
      </p>

      {isLoading && (
        <div className="w-full h-1 bg-gray-200 rounded overflow-hidden mb-2">
          <div className="h-full bg-blue-500 animate-pulse" style={{ width: '100%' }}></div>
        </div>
      )}

      <ExtraIncomeTable
        extraIncomes={extraIncomes}
        openActionMenuId={openActionMenuId}
        setOpenActionMenuId={setOpenActionMenuId}
        onEdit={(income) => {
          setSelectedExtraIncome(income);
          setIsModalOpen(true);
        }}
        onMarkAsPaid={(income) => {
          setSelectedExtraIncomeForPayment(income);
          setIsMarkAsPaidModalOpen(true);
        }}
        onSuccess={refresh}
      />

      <div className="flex justify-center items-center space-x-4 mt-4">
        <button
          disabled={currentPage === 1}
          onClick={() => setPage(currentPage - 1)}
          className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
        >
          Previous
        </button>
        <span>Page {currentPage} of {totalPages}</span>
        <button
          disabled={currentPage === totalPages}
          onClick={() => setPage(currentPage + 1)}
          className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>

      <AddExtraIncomeModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedExtraIncome(null);
        }}
        onSuccess={() => {
          setIsModalOpen(false);
          setSelectedExtraIncome(null);
          refresh();
        }}
        extraIncome={selectedExtraIncome}
        selectedOrder={
          selectedExtraIncome
            ? {
                label: selectedExtraIncome.order?.identifier,
                value: selectedExtraIncome.order_id,
              }
            : undefined
        }
      />

      <MarkExtraIncomeAsPaidModal
        isOpen={isMarkAsPaidModalOpen}
        onClose={() => {
          setIsMarkAsPaidModalOpen(false);
          setSelectedExtraIncomeForPayment(null);
        }}
        onSuccess={() => {
          setIsMarkAsPaidModalOpen(false);
          setSelectedExtraIncomeForPayment(null);
          refresh();
        }}
        extraIncome={selectedExtraIncomeForPayment}
      />
    </div>
  );
};

export default ExtraIncomes;
